<template>
<div>
  <navigation></navigation>
  <div class="checkout-page">
    <div class="container">
      <h1 class="page-title">Checkout</h1>

      <div v-if="cart.length === 0" class="empty-state">
        <p>Your cart is empty. Please add items before checking out.</p>
        <button class="btn-back" @click="$router.push('/shop')">
          Back to Shop
        </button>
      </div>

      <div v-else class="checkout-content">
        <div class="checkout-form">
          <div class="form-section">
            <h2>Shipping Information</h2>

            <div class="form-row">
              <div class="form-group">
                <label>First Name *</label>
                <input
                  v-model="shipping.first_name"
                  type="text"
                  required
                  :disabled="processing"
                />
              </div>

              <div class="form-group">
                <label>Last Name *</label>
                <input
                  v-model="shipping.last_name"
                  type="text"
                  required
                  :disabled="processing"
                />
              </div>
            </div>

            <div class="form-group">
              <label>Email *</label>
              <input
                v-model="shipping.email"
                type="email"
                required
                :disabled="processing"
              />
            </div>

            <div class="form-group">
              <label>Phone</label>
              <input
                v-model="shipping.phone"
                type="tel"
                :disabled="processing"
              />
            </div>

            <div class="form-group">
              <label>Address Line 1 *</label>
              <input
                v-model="shipping.address_line1"
                type="text"
                required
                :disabled="processing"
              />
            </div>

            <div class="form-group">
              <label>Address Line 2</label>
              <input
                v-model="shipping.address_line2"
                type="text"
                :disabled="processing"
              />
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>City *</label>
                <input
                  v-model="shipping.city"
                  type="text"
                  required
                  :disabled="processing"
                />
              </div>

              <div class="form-group">
                <label>State/Province *</label>
                <input
                  v-model="shipping.state"
                  type="text"
                  required
                  :disabled="processing"
                />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>Postal Code *</label>
                <input
                  v-model="shipping.postal_code"
                  type="text"
                  required
                  :disabled="processing"
                />
              </div>

              <div class="form-group">
                <label>Country *</label>
                <input
                  v-model="shipping.country"
                  type="text"
                  required
                  :disabled="processing"
                />
              </div>
            </div>
          </div>

          <div v-if="error" class="error-message">
            {{ error }}
          </div>

          <div v-if="processing" class="processing-message">
            {{ processingMessage }}
          </div>

          <div v-if="!processing && !orderCreated">
            <div ref="paypalButtons" id="paypal-button-container" class="paypal-button-container"></div>

            <div class="Spacer-module_divider__ewqEj">
              <span>OR</span>
            </div>

            <!-- Credit Card Fields -->
            <div id="card-form" class="card_container">
              <div id="card-name-field-container" class="card_field"></div>
              <div id="card-number-field-container" class="card_field"></div>
              <div id="card-expiry-field-container" class="card_field_50" style="display:inline-block"></div>
              <div id="card-cvv-field-container" class="card_field_50" style="display:inline-block"></div>
              <button
                id="card-field-submit-button"
                type="button"
                @click="submitCardPayment()"
                :disabled="cardSubmitDisabled"
              >
                {{ cardSubmitDisabled ? 'Processing...' : `Pay $${formatPrice(cartTotal)}` }}
              </button>
            </div>
          </div>
        </div>

        <div class="order-summary">
          <div class="summary-card">
            <h2>Order Summary</h2>

            <div class="summary-items">
              <div
                v-for="item in cart"
                :key="item.variantId"
                class="summary-item"
              >
                <div class="summary-item-image">
                  <img v-if="item.image" :src="item.image" :alt="item.name" />
                </div>
                <div class="summary-item-details">
                  <div class="summary-item-name">{{ item.name }}</div>
                  <div class="summary-item-variants">
                    <span v-if="item.color">{{ item.color }}</span>
                    <span v-if="item.size">{{ item.size }}</span>
                  </div>
                  <div class="summary-item-qty">Qty: {{ item.quantity }}</div>
                </div>
                <div class="summary-item-price">
                  ${{ formatPrice(item.price * item.quantity) }}
                </div>
              </div>
            </div>

            <div class="summary-divider"></div>

            <div class="summary-row">
              <span>Subtotal</span>
              <span>${{ formatPrice(cartTotal) }}</span>
            </div>

            <div class="summary-row">
              <span>Shipping</span>
              <span class="free-shipping">FREE</span>
            </div>

            <div class="summary-divider"></div>

            <div class="summary-row total">
              <span>Total</span>
              <span>${{ formatPrice(cartTotal) }}</span>
            </div>
          </div>

          <button class="btn-back-to-cart" @click="$router.push('/cart')">
            ← Back to Cart
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import ProductRepository from '../data/product.repository'
import Nav from '../components/Nav'

export default {
  name: 'Checkout',
  components: {
    'navigation': Nav
  },
  data () {
    return {
      shipping: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'US'
      },
      processing: false,
      processingMessage: '',
      error: null,
      orderCreated: false,
      currentOrderId: null,
      cardField: null,
      cardSubmitDisabled: false
    }
  },
  computed: {
    ...mapState(['cart', 'isAuthenticated', 'currentUser']),
    ...mapGetters(['cartTotal', 'cartItemCount'])
  },
  mounted () {
    // Check authentication
    if (!this.isAuthenticated) {
      this.$router.push('/login')
      return
    }

    // Pre-fill email from user if available
    if (this.currentUser && this.currentUser.email) {
      this.shipping.email = this.currentUser.email
    }

    const that = this

    // Define PayPal callback functions locally (matching Purchase.vue pattern)
    async function _createOrder () {
      // Validate form
      if (!that.validateForm()) {
        throw new Error('Please fill in all required fields')
      }

      try {
        // Prepare cart data for API
        const cartData = that.cart.map(item => ({
          variant_id: item.variantId,
          quantity: item.quantity,
          price: item.price
        }))

        // Transform shipping data to match backend expectations
        const shippingData = {
          name: `${that.shipping.first_name} ${that.shipping.last_name}`.trim(),
          email: that.shipping.email,
          phone: that.shipping.phone,
          address1: that.shipping.address_line1,
          address2: that.shipping.address_line2,
          city: that.shipping.city,
          state: that.shipping.state,
          postal_code: that.shipping.postal_code,
          country: that.shipping.country
        }

        // Create order via API
        const response = await ProductRepository.createOrder({
          cart: cartData,
          shipping: shippingData
        })

        console.log('Create order response:', response)

        if (response.paypal_order_id) {
          that.currentOrderId = response.order_id
          return response.paypal_order_id
        } else {
          const errorMessage = 'Failed to create PayPal order'
          console.error(errorMessage, response)
          throw new Error(errorMessage)
        }
      } catch (error) {
        console.error('Create order error:', error)
        that.error = 'There was an issue processing your payment'
        throw error
      }
    }

    async function _onApprove (data) {
      console.log('_onApprove called with data:', data)
      that.processingMessage = 'Processing payment...'

      try {
        console.log('Capturing payment for order ID:', that.currentOrderId)
        // Capture payment via API
        await ProductRepository.captureOrder(that.currentOrderId)

        console.log('Payment captured successfully')
        that.processingMessage = 'Payment successful! Redirecting...'

        // Clear cart
        that.$store.dispatch('clearCart')

        // Redirect to order confirmation
        // setTimeout(() => {
        that.$router.push(`/order-confirmation/${that.currentOrderId}`)
        // }, 1000)
      } catch (err) {
        that.processing = false
        that.error = (err.response && err.response.data && err.response.data.message) || 'Payment capture failed. Please contact support.'
        console.error('Capture payment error:', err)
        console.error('Capture error response:', err.response)
        throw err
      }
    }

    // Initialize PayPal (SDK is already loaded globally)
    this.$nextTick(() => {
      paypal.Buttons({
        createOrder: _createOrder,
        onApprove: _onApprove,
        onError: (err) => {
          that.processing = false
          that.error = 'Payment failed. Please try again.'
          console.error('PayPal error:', err)
        },
        style: {
          shape: 'rect',
          layout: 'vertical',
          color: 'gold',
          label: 'paypal'
        }
      }).render('#paypal-button-container')

      that.cardField = paypal.CardFields({
        createOrder: _createOrder,
        onApprove: _onApprove,
        style: {
          input: {
            'font-size': '16px',
            'font-family': 'helvetica, tahoma, calibri, sans-serif',
            color: '#333333'
          },
          '.invalid': { color: 'red' }
        }
      })

      if (that.cardField.isEligible()) {
        const nameField = that.cardField.NameField()
        nameField.render('#card-name-field-container')

        const numberField = that.cardField.NumberField()
        numberField.render('#card-number-field-container')

        const cvvField = that.cardField.CVVField()
        cvvField.render('#card-cvv-field-container')

        const expiryField = that.cardField.ExpiryField()
        expiryField.render('#card-expiry-field-container')
      } else {
        that.error = 'Card fields are not available at this time. Please reach out to support@stumpthespread.com'
      }
    })
  },
  methods: {

    submitCardPayment () {
      if (this.cardSubmitDisabled) return

      // Validate form first
      if (!this.validateForm()) {
        return
      }

      this.cardSubmitDisabled = true
      this.error = null

      this.cardField.submit({}).then((data) => {
        console.log(data)
      }).catch((err) => {
        console.error('Card payment error:', err)

        if (err.message && err.message.indexOf('DECLINED') > -1) {
          this.error = 'Payment declined. Please check your card details and try again.'
        } else if (err.message === 'INVALID_NUMBER') {
          this.error = 'Invalid card number. Please check and try again.'
        } else if (err.message === 'INVALID_EXPIRY') {
          this.error = 'Invalid expiration date. Please check and try again.'
        } else if (err.message === 'INVALID_CVV') {
          this.error = 'Invalid CVV. Please check and try again.'
        } else {
          this.error = err.message || 'Payment failed. Please check your card details and try again.'
        }
      }).finally(() => {
        this.cardSubmitDisabled = false
      })
    },

    validateForm () {
      const required = [
        'first_name',
        'last_name',
        'email',
        'address_line1',
        'city',
        'state',
        'postal_code',
        'country'
      ]

      for (const field of required) {
        if (!this.shipping[field] || this.shipping[field].trim() === '') {
          this.error = 'Please fill in all required fields'
          return false
        }
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(this.shipping.email)) {
        this.error = 'Please enter a valid email address'
        return false
      }

      return true
    },

    formatPrice (price) {
      return price.toFixed(2)
    }
  }
}
</script>

<style scoped>
.checkout-page {
  padding: 20px 0;
  min-height: 60vh;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.page-title {
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 30px;
  text-align: center;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-state p {
  font-size: 18px;
  color: #718096;
  margin-bottom: 24px;
}

.btn-back {
  background: #3182ce;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-back:hover {
  background: #2c5aa0;
}

.checkout-content {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 30px;
  margin-top: 20px;
}

/* Form Styles */
.checkout-form {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 24px;
}

.form-section h2 {
  font-size: 20px;
  font-weight: bold;
  margin: 0 0 20px 0;
  color: #2d3748;
}

.form-group {
  margin-bottom: 16px;
  flex: 1;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-group label {
  display: block;
  font-weight: 600;
  margin-bottom: 6px;
  color: #2d3748;
  font-size: 14px;
}

.form-group input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #cbd5e0;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: #3182ce;
}

.form-group input:disabled {
  background: #f7fafc;
  cursor: not-allowed;
}

.paypal-button-container {
  margin-top: 24px;
}

.loading-paypal,
.processing-message {
  text-align: center;
  padding: 24px;
  font-size: 16px;
  color: #4a5568;
}

.error-message {
  margin-top: 16px;
  padding: 12px;
  background: #fed7d7;
  border-left: 4px solid #e53e3e;
  border-radius: 4px;
  color: #742a2a;
  font-size: 14px;
}

/* Order Summary */
.order-summary {
  position: sticky;
  top: 20px;
  height: fit-content;
}

.summary-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 16px;
}

.summary-card h2 {
  font-size: 20px;
  font-weight: bold;
  margin: 0 0 20px 0;
  color: #2d3748;
}

.summary-items {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
}

.summary-item {
  display: grid;
  grid-template-columns: 60px 1fr auto;
  gap: 12px;
  align-items: center;
}

.summary-item-image {
  width: 60px;
  height: 60px;
  background: #f7fafc;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.summary-item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.summary-item-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-item-name {
  font-weight: 600;
  font-size: 14px;
  color: #2d3748;
}

.summary-item-variants {
  font-size: 12px;
  color: #718096;
}

.summary-item-variants span:not(:last-child)::after {
  content: ' • ';
}

.summary-item-qty {
  font-size: 12px;
  color: #718096;
}

.summary-item-price {
  font-weight: 600;
  color: #2d3748;
}

.summary-divider {
  height: 1px;
  background: #e2e8f0;
  margin: 16px 0;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 16px;
  color: #4a5568;
}

.summary-row.total {
  font-size: 20px;
  font-weight: bold;
  color: #2d3748;
}

.free-shipping {
  color: #48bb78;
  font-weight: 600;
}

.btn-back-to-cart {
  width: 100%;
  padding: 12px 24px;
  border: 1px solid #cbd5e0;
  background: white;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  color: #2d3748;
}

.btn-back-to-cart:hover {
  background: #f7fafc;
}

/* Responsive */
@media (max-width: 968px) {
  .checkout-content {
    grid-template-columns: 1fr;
  }

  .order-summary {
    position: static;
    order: -1;
  }
}

/* Payment Divider */
.Spacer-module_divider__ewqEj {
  align-items: center;
  margin: 0.3rem 0;
  display: flex;
  text-align: center;
}

.Spacer-module_divider__ewqEj:after,
.Spacer-module_divider__ewqEj:before {
  background-color: #c8c8c8;
  content: "";
  flex: 1;
  height: 1px;
  margin: 0 .675rem;
}

/* PayPal Button Container */
.paypal-button-container {
  border-radius: 5px;
  background-color: #dcdce1;
  padding-bottom: 4px;
  max-width: 760px;
  width: 100%;
  margin: 0 auto;
}

/* Card Form Container */
.card_container {
  border-radius: 5px;
  background-color: #dcdce1;
  padding: 10px 4px;
  max-width: 760px;
  width: 100%;
  margin: 0 auto;
}

.card_field {
  width: 100%;
  margin-bottom: 16px;
}

.card_field_50 {
  width: 50%;
  margin-bottom: 16px;
}

#card-field-submit-button {
  position: relative;
  border-radius: 1000px;
  color: #ffffff;
  cursor: pointer;
  display: inline-block;
  min-width: 6rem;
  text-align: center;
  text-decoration: none;
  transition: color 0.20s ease, background-color 0.20s ease, border-color 0.20s ease;
  border: 0.125rem solid #003087;
  font-family: PayPalOpen-Bold, "Helvetica Neue", Arial, sans-serif;
  font-size: 1.125rem;
  line-height: 1.5rem;
  font-weight: 400;
  background-color: #003087;
  padding: 0.625rem 1.875rem;
  width: 100%;
}

#card-field-submit-button:disabled {
  opacity: 0.5;
}

@media (max-width: 768px) {
  .page-title {
    font-size: 24px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .form-group input {
    padding: 8px 4px;
    font-size: 16px;
  }

  .form-group label {
    font-size: 13px;
  }

  .card_field_50 {
    width: 100%;
    display: block !important;
  }
}
</style>



// WEBPACK FOOTER //
// Checkout.vue?0fc9a62f